import { useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import Logo from '@/components/Logo';
import { api } from '@/lib/api';
import { useDebounced } from '@/lib/catalogue';
import { PROVINCES, formatCodePostal, formatTelephone } from '@/lib/clientForm';
import { formatMoney } from '@/lib/utils';
import {
  useCreerCommande,
  useCreerWalkIn,
  useCustomers,
  useMettreAJourClient,
  type ClientRequis,
  type Customer,
} from '@/lib/orders';

/** Un brouillon reprenable proposé au rep (le sien, ou celui d'un collègue de ligne). */
type Brouillon = {
  id: number;
  lineCount: number;
  total: string;
  createdAt: string;
  repName: string;
  mine: boolean;
};

/**
 * Champs OBLIGATOIRES d'un client avant de commencer sa commande. Un client
 * (nouveau ou existant) qui n'a pas tout doit être complété d'abord ; s'il a
 * déjà tout, on passe directement à la commande.
 */
const CHAMPS_REQUIS = ['name', 'phone', 'address', 'city', 'province', 'postalCode'] as const;
const estComplet = (c: Customer) =>
  CHAMPS_REQUIS.every((f) => String(c[f] ?? '').trim() !== '');

/** Plan §5.3 — sélection du client : recherche par nom, n° ou ville, ou création walk-in. */
export default function NouvelleCommande() {
  const [q, setQ] = useState('');
  const [walkIn, setWalkIn] = useState(false);
  const [completer, setCompleter] = useState<Customer | null>(null);
  // Client ayant déjà des brouillons : on propose de reprendre ou d'en créer un.
  const [choix, setChoix] = useState<{ client: Customer; drafts: Brouillon[] } | null>(null);
  const [ouverture, setOuverture] = useState(false);
  const debounced = useDebounced(q, 250);
  // On ne liste pas tout le carnet d'adresses : les clients n'apparaissent qu'une
  // fois une recherche saisie. La requête est désactivée tant que le champ est vide.
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);
  const creer = useCreerCommande();
  const navigate = useNavigate();

  async function demarrerNouvelle(client: Customer) {
    const { order } = await creer.mutateAsync(client.id);
    void navigate(`/order/${order.id}`);
  }

  async function ouvrir(client: Customer) {
    // S'il existe déjà des brouillons pour ce client, accessibles au rep (les siens
    // ou ceux d'un collègue de la même ligne), on laisse CHOISIR : reprendre l'un
    // d'eux ou commencer une nouvelle commande. Sinon on démarre directement.
    setOuverture(true);
    try {
      const { drafts } = await api<{ drafts: Brouillon[] }>(
        `/api/orders/for-customer/${client.id}`,
      );
      if (drafts.length === 0) {
        await demarrerNouvelle(client);
        return;
      }
      setChoix({ client, drafts });
    } finally {
      setOuverture(false);
    }
  }

  // Sélection d'un client existant : s'il est complet on démarre la commande,
  // sinon on demande d'abord de compléter ses coordonnées.
  function choisir(client: Customer) {
    if (estComplet(client)) void ouvrir(client);
    else setCompleter(client);
  }

  return (
    <div className="min-h-full">
      <header className="bg-card sticky top-0 z-10 space-y-3 border-b p-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="border-input flex h-14 items-center rounded-lg border px-4 font-medium">
            ← Accueil
          </Link>
          <Logo className="h-9" />
          <h1 className="text-xl font-bold">Nouvelle commande</h1>
        </div>

        {!completer && !choix && (
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom du client, n° de client ou ville…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-lg focus:ring-2 focus:outline-none"
          />
        )}
      </header>

      <main className="space-y-3 p-4">
        {creer.isError && (
          <p className="text-destructive font-medium">{(creer.error as Error).message}</p>
        )}

        {choix ? (
          // Le client a déjà des brouillons : reprendre l'un d'eux ou en créer un.
          <section className="bg-card space-y-4 rounded-xl border p-4">
            <div>
              <h2 className="font-semibold">{choix.client.name}</h2>
              <p className="text-muted-foreground text-sm">
                Ce client a déjà {choix.drafts.length} commande
                {choix.drafts.length > 1 ? 's' : ''} brouillon. Reprendre l'une d'elles ou en
                commencer une nouvelle ?
              </p>
            </div>

            <ul className="space-y-2">
              {choix.drafts.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => navigate(`/order/${d.id}`)}
                    className="bg-background hover:border-primary/50 flex w-full flex-col rounded-lg border p-3 text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">
                        Reprendre — {d.lineCount} article{d.lineCount > 1 ? 's' : ''}
                      </span>
                      <span className="font-semibold tabular-nums">{formatMoney(d.total)}</span>
                    </div>
                    <span className="text-muted-foreground text-sm">
                      Commencé le{' '}
                      {new Date(d.createdAt).toLocaleString('fr-CA', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                      {!d.mine && ` · brouillon de ${d.repName}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => void demarrerNouvelle(choix.client)}
                disabled={creer.isPending}
                className="bg-primary text-primary-foreground h-14 rounded-lg px-6 font-semibold disabled:opacity-40"
              >
                {creer.isPending ? 'Création…' : 'Commencer une nouvelle commande'}
              </button>
              <button
                onClick={() => setChoix(null)}
                className="border-input h-14 rounded-lg border px-5 font-medium"
              >
                Retour
              </button>
            </div>
          </section>
        ) : completer ? (
          // Compléter un client existant incomplet avant de démarrer sa commande.
          <FormulaireClient initial={completer} onPret={ouvrir} onAnnuler={() => setCompleter(null)} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {!recherche
                  ? ''
                  : clients.isPending
                    ? 'Recherche…'
                    : `${clients.data?.total ?? 0} client(s)`}
              </p>
              <button
                onClick={() => setWalkIn(!walkIn)}
                className="border-input h-12 rounded-lg border px-4 font-medium"
              >
                {walkIn ? 'Annuler' : 'Client absent de la liste'}
              </button>
            </div>

            {walkIn && <FormulaireClient onPret={ouvrir} />}

            {!recherche ? (
              !walkIn && (
                <div className="rounded-xl border border-dashed p-10 text-center">
                  <p className="font-medium">Recherchez un client</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Tapez un nom, un n° de client ou une ville — ou utilisez « Client absent de la
                    liste ».
                  </p>
                </div>
              )
            ) : (
              <>
                <div className="grid gap-3 lg:grid-cols-2">
                  {clients.data?.customers.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => choisir(c)}
                      disabled={creer.isPending || ouverture}
                      className="bg-card hover:border-primary/50 active:bg-accent flex min-h-20 w-full flex-col justify-center rounded-xl border p-4 text-left disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                          {c.custNum}
                        </span>
                        {c.isWalkIn && (
                          <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                            walk-in
                          </span>
                        )}
                        {!estComplet(c) && (
                          <span className="border-input rounded border px-2 py-0.5 text-xs font-medium">
                            à compléter
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-lg font-medium">{c.name}</p>
                      {(c.city || c.address) && (
                        <p className="text-muted-foreground text-sm">
                          {[c.address, c.city, c.province].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>

                {!clients.isPending && clients.data?.customers.length === 0 && (
                  <div className="rounded-xl border border-dashed p-10 text-center">
                    <p className="font-medium">Aucun client trouvé.</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Vérifiez l'orthographe, ou créez un client walk-in.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

type ClientForm = ClientRequis & { email: string };

/**
 * Coordonnées d'un client. Sans `initial` : crée un client walk-in. Avec
 * `initial` : complète/corrige un client existant. Les 7 champs de base sont
 * obligatoires (le courriel est facultatif) avant de commencer la commande.
 * Tout est saisi EN MAJUSCULES ; le téléphone et le code postal sont formatés.
 */
function FormulaireClient({
  initial,
  onPret,
  onAnnuler,
}: {
  initial?: Customer;
  onPret: (c: Customer) => void;
  onAnnuler?: () => void;
}) {
  const [form, setForm] = useState<ClientForm>({
    name: (initial?.name ?? '').toUpperCase(),
    phone: formatTelephone(initial?.phone ?? ''),
    address: (initial?.address ?? '').toUpperCase(),
    city: (initial?.city ?? '').toUpperCase(),
    province: (initial?.province ?? '').toUpperCase(),
    postalCode: formatCodePostal(initial?.postalCode ?? ''),
    email: (initial?.email ?? '').toUpperCase(),
  });
  const creer = useCreerWalkIn();
  const maj = useMettreAJourClient();
  const enCours = creer.isPending || maj.isPending;
  const erreur = (creer.error || maj.error) as Error | undefined;

  const set = (champ: keyof ClientForm, valeur: string) => setForm((f) => ({ ...f, [champ]: valeur }));
  // Saisie en MAJUSCULES pour les champs texte libres.
  const enMaj = (champ: keyof ClientForm) => (e: ChangeEvent<HTMLInputElement>) =>
    set(champ, e.target.value.toUpperCase());

  const cls = 'border-input bg-background h-14 w-full rounded-lg border px-3 text-base';

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const { customer } = initial
          ? await maj.mutateAsync({ id: initial.id, ...form })
          : await creer.mutateAsync(form);
        onPret(customer);
      }}
      className="bg-card space-y-4 rounded-xl border p-4"
    >
      <div>
        <h2 className="font-semibold">
          {initial ? 'Compléter les coordonnées du client' : 'Nouveau client sur place'}
        </h2>
        <p className="text-muted-foreground text-sm">
          {initial
            ? 'Ces informations sont obligatoires avant de commencer la commande.'
            : 'Coordonnées obligatoires. Un numéro WI-… sera attribué, à rapprocher de l\'ERP après le show.'}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Nom du client *</span>
          <input value={form.name} onChange={enMaj('name')} required className={cls} />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Téléphone *</span>
          <input
            value={form.phone}
            onChange={(e) => set('phone', formatTelephone(e.target.value))}
            inputMode="tel"
            placeholder="(514) 555-1234"
            pattern="\(\d{3}\) \d{3}-\d{4}"
            title="Format : (514) 555-1234"
            required
            className={cls}
          />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Adresse *</span>
          <input value={form.address} onChange={enMaj('address')} required className={cls} />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Ville *</span>
          <input value={form.city} onChange={enMaj('city')} required className={cls} />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Province *</span>
          <select
            value={form.province}
            onChange={(e) => set('province', e.target.value)}
            required
            className={cls}
          >
            <option value="">—</option>
            {PROVINCES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Code postal *</span>
          <input
            value={form.postalCode}
            onChange={(e) => set('postalCode', formatCodePostal(e.target.value))}
            placeholder="H0H 0H0"
            pattern="[A-Z]\d[A-Z] \d[A-Z]\d"
            title="Format : H0H 0H0"
            required
            className={cls}
          />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Courriel</span>
          <input
            value={form.email}
            onChange={enMaj('email')}
            inputMode="email"
            placeholder="NOM@DOMAINE.COM"
            pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
            title="Adresse valide, ex. NOM@DOMAINE.COM"
            className={cls}
          />
        </label>
      </div>

      {erreur && <p className="text-destructive text-sm">{erreur.message}</p>}

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={enCours}
          className="bg-primary text-primary-foreground h-14 rounded-lg px-6 font-semibold disabled:opacity-40"
        >
          {enCours ? 'Enregistrement…' : 'Enregistrer et commencer la commande'}
        </button>
        {onAnnuler && (
          <button
            type="button"
            onClick={onAnnuler}
            className="border-input h-14 rounded-lg border px-5 font-medium"
          >
            Annuler
          </button>
        )}
      </div>
    </form>
  );
}
